import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  DialogActions,
} from '@mui/material';
import { useGetCustomerListForSelection } from '@/hooks/requests/useCustomers';
import { useForm } from 'react-hook-form';
import { useTransactionFormModal } from '@/hooks/stores/useTransactionFormModal';
import { useOwnerSelectFormModal } from '@/hooks/stores/useOwnerSelectFormModal';
import { SVForm, SVAutocomplete, SVButton, SVLoader } from '@/components/core';
import { useGetBankAccountDetails } from '@/hooks/requests/useBankAccounts';
import { SelectOption } from '@/types/inputs';
import { useEffect } from 'react';
import { StatusQueryParam } from '@/types/common';

type OwnerFormValues = {
  customerId: string;
};

export default function OwnerSelectModal() {
  const { openTransactionFormModal } = useTransactionFormModal();
  const { open, type, bankAccountId, closeOwnerSelectFormModal } =
    useOwnerSelectFormModal();

  const formMethods = useForm<OwnerFormValues>({
    defaultValues: { customerId: '' },
    mode: 'onSubmit',
  });

  const useCustomerListForSelection = open && !bankAccountId;
  const { accountDetails, accountsDetailsIsPending } =
    useGetBankAccountDetails(bankAccountId);
  const { customerListByBoth, isCustomerListPending } =
    useGetCustomerListForSelection(
      {
        status: StatusQueryParam.ACTIVE,
      },
      useCustomerListForSelection,
    );
  const isLoading = useCustomerListForSelection
    ? isCustomerListPending
    : accountsDetailsIsPending;

  useEffect(() => {
    formMethods.reset();
  }, [open]);

  const mappedOwners: SelectOption[] = accountDetails
    ? accountDetails.owners.map(owner => {
        return {
          label: `${owner.profile.fullName} (${owner.profile.email})`,
          value: owner.id,
        };
      })
    : [];

  function handleOwnerMenuNext(data: OwnerFormValues) {
    closeOwnerSelectFormModal();
    openTransactionFormModal({
      bankAccountId,
      customerId: data.customerId,
      type,
    });
  }

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={closeOwnerSelectFormModal}>
      <SVForm methods={formMethods} onSubmit={handleOwnerMenuNext}>
        <DialogContent>
          {isLoading ? (
            <SVLoader />
          ) : (
            <Box>
              <Typography variant="h6" component="h2">
                New Request
              </Typography>
              <Typography sx={{ mt: 2 }}>
                Which member's accounts would you like to use for moving funds?
              </Typography>
              <SVAutocomplete
                name="customerId"
                label="Select Account Owner"
                options={bankAccountId ? mappedOwners : customerListByBoth}
              />
              <Typography variant="caption">
                Note: All owners on the account will receive a transaction
                requests
              </Typography>
            </Box>
          )}
        </DialogContent>
        {!isLoading && (
          <DialogActions>
            <SVButton onClick={closeOwnerSelectFormModal} variant="tertiary">
              Cancel
            </SVButton>
            <SVButton onClick={formMethods.handleSubmit(handleOwnerMenuNext)}>
              Next
            </SVButton>
          </DialogActions>
        )}
      </SVForm>
    </Dialog>
  );
}
